import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowLeft, Tag, Eye, Share2, Check } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const lang = (i18n.language || 'fr').split('-')[0];
  const intlLocale = ({ fr: 'fr-CA', en: 'en-CA', es: 'es-ES', ar: 'ar' })[lang] || 'fr-CA';
  const pick = (field) => (lang !== 'fr' && post?.[`${field}_en`] ? post[`${field}_en`] : post?.[field]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/api/blog/posts/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setPost(data);
        }
      } catch (err) {
        console.error('Error fetching post:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: pick('title'), url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <p className="text-slate-500 text-lg">{t('blog.notFound')}</p>
          <Link to="/blog" className="text-blue-600 hover:underline">{t('blog.backToBlog')}</Link>
        </div>
      </>
    );
  }

  const title = pick('title');
  const description = pick('meta_description') || pick('excerpt');

  return (
    <>
      <SEO
        title={title}
        description={description}
        url={`https://entrepotautomontreal.ca/blog/${post.slug}`}
        keywords={post.meta_keywords?.join(', ') || ''}
        image={post.cover_image || undefined}
        type="article"
        article={{
          publishedTime: post.published_at,
          author: post.author
        }}
      />
      <Navbar />
      
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0c2340] to-[#1a4f85] text-white py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-blue-200 hover:text-white text-sm mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4" /> {t('blog.backToBlog')}
            </Link>
            <h1 className="text-2xl md:text-3xl font-black leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-blue-200 text-sm">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {post.published_at ? new Date(post.published_at).toLocaleDateString(intlLocale, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
              </span>
              {post.views > 0 && (
                <span className="flex items-center gap-1.5">
                  <Eye className="h-4 w-4" /> {post.views} {t('blog.reads')}
                </span>
              )}
              <button onClick={handleShare} className="flex items-center gap-1.5 hover:text-white transition-colors">
                {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
                {copied ? t('blog.copied') : t('blog.share')}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 py-10">
          {post.cover_image && (
            <img src={post.cover_image.replace('/upload/', '/upload/w_1200,h_630,c_fill,q_auto,f_auto/')} alt={title} className="w-full aspect-[1200/630] object-cover rounded-xl border border-slate-200 mb-6" data-testid="blog-post-cover" />
          )}
          <article 
            className="bg-white rounded-xl border border-slate-200 p-6 md:p-10"
            data-testid="blog-post-content"
          >
            <div 
              className="text-slate-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-[#0c2340] [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-[#0c2340] [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:mb-4 [&_p]:text-slate-700 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-2 [&_li]:text-slate-700 [&_strong]:text-slate-900 [&_a]:text-blue-600 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: pick('content') }}
            />
          </article>
          
          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs px-3 py-1.5 rounded-full">
                  <Tag className="h-3 w-3" /> {tag}
                </span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-gradient-to-br from-[#0c2340] to-[#1a4f85] rounded-xl p-6 text-center text-white">
            <h3 className="text-xl font-bold mb-2">{t('blog.ctaTitle')}</h3>
            <p className="text-blue-200 text-sm mb-4">{t('blog.ctaSubtitle')}</p>
            <Link
              to="/inventory"
              className="inline-block bg-white text-[#0c2340] font-bold px-6 py-2.5 rounded-full hover:bg-blue-50 transition-colors"
            >
              {t('blog.ctaButton')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPostPage;
